import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CookieService } from '~shared/services/cookie.service';
import optimizelySdk, { Client, UserAttributes } from '@optimizely/optimizely-sdk';

import { environment } from '~env/environment';
import { NUMBER_10, NUMBER_1000, NUMBER_2 } from '~shared/constants';
import { METRO_COOKIE_NAME } from '~shared/constants/buyer-cookie.constant';
import { ApplicationEventService } from '~shared/modules/application-event/services';
import { OptimizelyAttributesUtil } from '~shared/utils/optimizely-attributes.util';
import { OptimizelyMethodTypeEnum } from '~shared/modules/application-event/constants';
import { OptimizelyExperimentApplicationEvent } from '~shared/modules/application-event/models';

const MAX_CALLS = NUMBER_2;

@Injectable({
  providedIn: 'root',
})
export class OptimizelyService {
  private count = {};
  private client: Client | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private cookieService: CookieService,
    private applicationEventService: ApplicationEventService
  ) {}

  public initialize(forceRecreation?: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (!this.client || forceRecreation) {
      const isCookieAccepted = this.cookieService.get(METRO_COOKIE_NAME)?.includes?.('|performance|');

      if (isCookieAccepted) {
        this.client = this.createClient();
      }
    }
  }

  public getExperimentVariant<T extends string>(
    experimentId: string,
    userAttributes?: UserAttributes,
    featureFlagGAEvent?: string
  ): Observable<T | null> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(null);
    }
    let optimizelyMethodCalled;
    return this.getOptimizelyBag().pipe(
      map(([optimizelyClient, userId]) => {
        let defaultUserAttributes = {};

        defaultUserAttributes = OptimizelyAttributesUtil.getDefaultAttributes();
        let variant;
        const attributes = { ...defaultUserAttributes, ...userAttributes };

        if (!environment.production) {
          this.count[experimentId] = (this.count[experimentId] || 0) + 1;

          if (this.count[experimentId] > NUMBER_2) {
            console.warn(`The experiment ${experimentId} has been called more than ${MAX_CALLS} times.
            Check if it's correct.`);
          }
        }

        if (localStorage.getItem(experimentId)) {
          variant = optimizelyClient?.getVariation(experimentId, userId, attributes);
          optimizelyMethodCalled = OptimizelyMethodTypeEnum.GET_VARIANT;
        } else {
          variant = optimizelyClient?.activate(experimentId, userId, attributes);
          optimizelyMethodCalled = OptimizelyMethodTypeEnum.ACTIVATE;
        }

        if (variant && variant !== 'undefined' && variant !== 'false' && variant !== 'null') {
          localStorage.setItem(experimentId, 'true');
          this.sendGaEventOptimizely(optimizelyMethodCalled, experimentId, variant, featureFlagGAEvent);
        }

        return variant as T | null;
      })
    );
  }

  public getOptimizelyBag: () => Observable<[Client, string]> = () => {
    return this.getClient().pipe(
      switchMap(async (client) => {
        await client?.onReady();

        const userId = this.cookieService.getOptimizelyUserId();

        const optimizelyBag: [Client, string] = [client, userId];
        return optimizelyBag;
      })
    );
  };

  public createClient(): Client {
    const optimizelyClientInstance = optimizelySdk.createInstance({
      sdkKey: environment?.optimizelySdkKey?.(),
      datafile: (window as any)?.optimizelyDatafile,
      eventBatchSize: NUMBER_10,
      eventFlushInterval: NUMBER_1000,
    });

    const userId = this.cookieService.getOptimizelyUserId();

    if (userId) {
      const attributes = OptimizelyAttributesUtil.getDefaultAttributes();

      (window as any).optimizely_user = optimizelyClientInstance.createUserContext(userId, attributes);
    }

    return optimizelyClientInstance;
  }

  private sendGaEventOptimizely(
    optimizelyMethodCalled: OptimizelyMethodTypeEnum,
    experimentId: string,
    capturedVariation: string,
    featureFlag?: string
  ): void {
    const event = new OptimizelyExperimentApplicationEvent(optimizelyMethodCalled, experimentId, capturedVariation);

    if (featureFlag) {
      this.applicationEventService.publishIfFFEnabled(featureFlag, event);
    } else {
      this.applicationEventService.publish(event);
    }
  }

  private getClient(): Observable<Client> {
    this.initialize();
    return of(this.client);
  }
}
